import {
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from "@chakra-ui/react";
import { keepPreviousData } from "@tanstack/react-query";
import { useInternalIntegrationTestingConfigurationCreate } from "gen/endpoints/integration-testing-configuration/integration-testing-configuration";
import {
  useInternalIntegrationTestingConfigurationCustomerOptions,
  useInternalIntegrationTestingConfigurationPathOptions,
} from "gen/endpoints/integration-testing-configuration-options/integration-testing-configuration-options";
import { IntegrationTestingConfigurationCreateDataRequest } from "gen/model/integrationTestingConfigurationCreateDataRequest";
import { IntegrationTestVersionTypeEnum } from "gen/model/integrationTestVersionTypeEnum";
import { useEffect, useMemo } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ButtonOrder } from "shared/components/button/ButtonOrder";
import { FormCancelButton } from "shared/components/button/FormCancelButton";
import { FormSubmitButton } from "shared/components/button/FormSubmitButton";
import { FormBooleanToggleButtonGroup } from "shared/components/button/ToggleButton";
import Form from "shared/components/form/Form";
import { FormSelect } from "shared/components/input/select/Select";
import { SelectOption } from "shared/components/input/select/SelectOption";

export const IntegrationTestingConfigurationForm: React.FC<
  React.PropsWithChildren<{
    isOpen: boolean;
    onSuccess: () => void;
    onClose: () => void;
  }>
> = ({ isOpen, onSuccess, onClose }) => {
  const { t } = useTranslation(["internal", "common"]);
  const { mutateAsync: createConfiguration } =
    useInternalIntegrationTestingConfigurationCreate();

  const form = useForm<IntegrationTestingConfigurationCreateDataRequest>({
    defaultValues: {
      version_type: IntegrationTestVersionTypeEnum.v2,
      enabled: false,
      export_enabled: true,
    },
  });

  const versionType = form.watch("version_type");

  const isV2 = versionType == IntegrationTestVersionTypeEnum.v2;
  useEffect(() => {
    if (isV2) {
      form.setValue("export_enabled", true);
    }
  }, [form, isV2, versionType]);

  const customerName = useWatch({
    control: form.control,
    name: "customer_name",
  });

  const { data: customerOptionData } =
    useInternalIntegrationTestingConfigurationCustomerOptions(
      {
        version_type: versionType,
      },
      {
        query: {
          enabled: !!versionType,
          placeholderData: keepPreviousData,
        },
      },
    );

  const { data: templatePathOptionData } =
    useInternalIntegrationTestingConfigurationPathOptions(
      {
        customer_name: customerName,
        version_type: versionType,
      },
      {
        query: {
          enabled: !!customerName,
          placeholderData: keepPreviousData,
        },
      },
    );

  const customerOptions = useMemo(
    () =>
      customerOptionData?.map((customerOption) => (
        <SelectOption
          key={`customer_configuration_option_${customerOption.value}`}
          value={customerOption.value}
        >
          {customerOption.rich_value}
        </SelectOption>
      )),
    [customerOptionData],
  );

  const templatePathOptions = useMemo(
    () =>
      templatePathOptionData?.map((templatePathOption) => (
        <SelectOption
          key={`shadow_path_configuration_option_${templatePathOption.value}`}
          value={templatePathOption.value}
        >
          {templatePathOption.rich_value}
        </SelectOption>
      )),
    [templatePathOptionData],
  );

  const onSubmit = async (
    data: IntegrationTestingConfigurationCreateDataRequest,
  ) => {
    await createConfiguration({ data });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <Form
          form={form}
          onSubmit={onSubmit}
          onSuccess={onSuccess}
          successMessage={t(
            "internal:integrationTesting.configuration.add.success",
          )}
        >
          <ModalHeader>
            {t("internal:integrationTesting.configuration.add.title")}
          </ModalHeader>
          <ModalBody>
            <FormSelect
              name="version_type"
              title={t(
                "internal:integrationTesting.configuration.form.integrationTestVersion",
              )}
            >
              <SelectOption value={IntegrationTestVersionTypeEnum.v1}>
                {IntegrationTestVersionTypeEnum.v1}
              </SelectOption>
              <SelectOption value={IntegrationTestVersionTypeEnum.v2}>
                {IntegrationTestVersionTypeEnum.v2}
              </SelectOption>
            </FormSelect>
            {customerOptions && (
              <FormSelect
                name="customer_name"
                title={t(
                  "internal:integrationTesting.configuration.form.customerName",
                )}
              >
                {customerOptions}
              </FormSelect>
            )}
            {templatePathOptions && (
              <FormSelect
                name="shadow_path"
                title={t(
                  "internal:integrationTesting.configuration.form.templatePath",
                )}
              >
                {templatePathOptions}
              </FormSelect>
            )}
            <FormBooleanToggleButtonGroup
              title={t("common:enabled")}
              name="enabled"
              mb={6}
              trueText={t("common:yes")}
              falseText={t("common:no")}
              defaultValue="false"
            />
            <FormBooleanToggleButtonGroup
              title={t(
                "internal:integrationTesting.configuration.form.exportEnabled",
              )}
              name="export_enabled"
              mb={6}
              trueText={t("common:yes")}
              falseText={t("common:no")}
              isDisabled={isV2}
              defaultValue={isV2 ? "true" : "false"}
            />
          </ModalBody>
          <ModalFooter gap={2}>
            <ButtonOrder
              dismiss={<FormCancelButton onClick={onClose} />}
              confirm={
                <FormSubmitButton>{t("common:submit")}</FormSubmitButton>
              }
            />
          </ModalFooter>
        </Form>
      </ModalContent>
    </Modal>
  );
};
